import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { CircularProgress, Grid } from '@material-ui/core';

import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { TextField } from 'core/components/text-field';
import { Tooltip } from 'core/components/tooltip';
import { GET_OFFER, GET_OFFER_DIFFERENCE } from 'core/store/offer-store/offer-queries';
import { buildOfferDiffChanges, dateLabel, extractOfferId, orderOffersForDiff } from './helpers';
import { CompareHeaderMeta, OfferDiffTable, OfferIdPairCard } from './shared-components';
import useStyles from './compare-offer.styles';

const CompareArbitraryOffers = () => {
  const classes = useStyles();
  const { offerId } = useParams();
  const history = useHistory();
  const location = useLocation();

  // Read once on mount; afterwards local state drives the comparison and the URL
  // is kept in sync via history.replace so the view stays shareable/reloadable.
  const [initialParams] = useState(() => new URLSearchParams(location.search));
  const initialA = initialParams.get('compareA');
  const initialB = initialParams.get('compareB');
  const hasInitialPair = Boolean(initialA && initialB);

  const [leftInput, setLeftInput] = useState(initialA ?? '');
  const [rightInput, setRightInput] = useState(initialB ?? offerId);
  const [submittedPair, setSubmittedPair] = useState(hasInitialPair ? { a: initialA, b: initialB } : null);
  const [entryError, setEntryError] = useState(null);

  const {
    data: currentData,
    loading: currentLoading,
    error: currentError
  } = useQuery(GET_OFFER, {
    variables: { offerId },
    fetchPolicy: 'no-cache'
  });

  const currentOffer = currentData?.offer;
  const parentOfferId = currentOffer?.parentId;

  const pairA = submittedPair?.a;
  const pairB = submittedPair?.b;
  const reuseCurrentForA = pairA === offerId;
  const reuseCurrentForB = pairB === offerId;

  const {
    data: pairAData,
    loading: pairALoading,
    error: pairAError
  } = useQuery(GET_OFFER, {
    variables: { offerId: pairA },
    skip: !pairA || reuseCurrentForA,
    fetchPolicy: 'no-cache'
  });

  const {
    data: pairBData,
    loading: pairBLoading,
    error: pairBError
  } = useQuery(GET_OFFER, {
    variables: { offerId: pairB },
    skip: !pairB || reuseCurrentForB,
    fetchPolicy: 'no-cache'
  });

  const offerA = reuseCurrentForA ? currentOffer : pairAData?.offer;
  const offerB = reuseCurrentForB ? currentOffer : pairBData?.offer;

  const orderedPair = offerA && offerB ? orderOffersForDiff(offerA, offerB) : null;
  const diffIds = orderedPair && { oldOfferId: orderedPair.oldOffer.id, newOfferId: orderedPair.newOffer.id };

  const {
    data: diffData,
    loading: diffLoading,
    error: diffError
  } = useQuery(GET_OFFER_DIFFERENCE, {
    variables: diffIds || {},
    skip: !diffIds,
    fetchPolicy: 'network-only'
  });

  // Prefill the first field with the parent offer ID once the offer loads, unless
  // the user (or a reloaded comparison) already put something there.
  useEffect(() => {
    if (parentOfferId) {
      setLeftInput((prev) => prev || parentOfferId);
    }
  }, [parentOfferId]);

  const onRunCompare = useCallback(() => {
    const a = extractOfferId(leftInput);
    const b = extractOfferId(rightInput);
    if (!a || !b) {
      setEntryError('Enter two offer IDs to compare.');
      return;
    }
    if (a === b) {
      setEntryError('Enter two different offer IDs.');
      return;
    }
    setLeftInput(a);
    setRightInput(b);
    setEntryError(null);
    setSubmittedPair({ a, b });
    history.replace({ search: `?${new URLSearchParams({ compareA: a, compareB: b })}` });
  }, [leftInput, rightInput, history]);

  const changes = useMemo(() => buildOfferDiffChanges(diffData), [diffData]);

  if (currentLoading) {
    return (
      <div className={classes.page}>
        <CircularProgress />
      </div>
    );
  }

  if (currentError || !currentOffer) {
    return (
      <div className={classes.errorContainer}>
        <Label type="title">Offer not found</Label>
        <Label type="body1">
          The offer could not be loaded. It may be invalid or you may not have permission to view it.
        </Label>
        <Label type="body1" className={classes.errorId}>
          {offerId}
        </Label>
      </div>
    );
  }

  const sidesLoading = pairALoading || pairBLoading;
  const isLoading = diffLoading || sidesLoading;
  const missingSideIds = submittedPair && !sidesLoading ? [!offerA && pairA, !offerB && pairB].filter(Boolean) : [];
  const sideLoadFailed = Boolean(pairAError || pairBError);
  const hasError = diffError || diffData?.getOfferDifference?.success === false;

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <Label type="title">Compare offers</Label>
        <CompareHeaderMeta offer={currentOffer} />

        <Card type="primaryEditable" className={classes.entryCard}>
          <Label type="body1" className={classes.entryPrompt}>
            {parentOfferId
              ? 'Enter any two offer IDs to compare. The offer created first is shown as the previous offer.'
              : 'This offer does not have a parent offer to compare against. You can still enter any two offer IDs to compare — the offer created first is shown as the previous offer.'}
          </Label>
          <Grid container direction="row" alignItems="flex-end" className={classes.entryRow}>
            <TextField
              mode="light"
              label="First offer ID"
              value={leftInput}
              onChange={(e) => setLeftInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onRunCompare()}
              className={classes.entryField}
            />
            <TextField
              mode="light"
              label="Second offer ID"
              value={rightInput}
              onChange={(e) => setRightInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onRunCompare()}
              className={classes.entryField}
            />
            <Button variant="contained" color="secondary" onClick={onRunCompare} className={classes.compareButton}>
              Compare
            </Button>
          </Grid>
          {entryError && (
            <Label type="body1" className={classes.entryError}>
              {entryError}
            </Label>
          )}
        </Card>

        {orderedPair && (
          <OfferIdPairCard
            previousDate={dateLabel(orderedPair.oldOffer.initialCreationDateTime)}
            previousOfferId={diffIds.oldOfferId}
            newerDate={dateLabel(orderedPair.newOffer.initialCreationDateTime)}
            newerOfferId={diffIds.newOfferId}
          />
        )}
      </div>

      {isLoading && <CircularProgress />}

      {!isLoading && missingSideIds.length > 0 && (
        <div className={classes.noChanges}>
          <Label type="body1" className={classes.errorText}>
            {sideLoadFailed
              ? 'Something went wrong loading the offers. Run the comparison again.'
              : "We couldn't find that offer. Check the ID and try again."}
            <Tooltip text="Copy the URL from your browser while viewing the offer." placement="top" />
          </Label>
        </div>
      )}

      {!isLoading && missingSideIds.length === 0 && hasError && (
        <div className={classes.noChanges}>
          <Label type="body1">
            {diffData?.getOfferDifference?.error ?? 'The offer difference could not be loaded.'}
          </Label>
        </div>
      )}

      {!isLoading && missingSideIds.length === 0 && !hasError && Boolean(diffIds) && changes.length === 0 && (
        <Label type="body1" className={classes.noChanges}>
          No differences found between these two offers.
        </Label>
      )}

      {!isLoading && missingSideIds.length === 0 && !hasError && changes.length > 0 && (
        <OfferDiffTable oldOfferId={diffIds?.oldOfferId} newOfferId={diffIds?.newOfferId} changes={changes} />
      )}
    </div>
  );
};

export default CompareArbitraryOffers;
